import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronUp } from "lucide-react";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    id: "painting-time",
    question: "Сколько времени занимает создание картины на заказ?",
    answer: "Сроки выполнения зависят от размера и сложности работы: картины 30×40 см выполняю за 2-4 дня, 50×70 см — за 5-8 дней. Для крупных работ может потребоваться 2-3 недели. Возможно выполнение срочных заказов по договоренности.",
    category: "Сроки и процесс"
  },
  {
    id: "painting-price",
    question: "Стоимость картин маслом и акрилом",
    answer: "Стоимость картины зависит от нескольких факторов: размера холста, сложности сюжета, выбранной техники и количества деталей. Простые композиции от 4000₽, детализированные работы от 8000₽. Окончательная цена обсуждается индивидуально в зависимости от ваших пожеланий.",
    category: "Стоимость"
  },
  {
    id: "materials-delivery",
    question: "Какие материалы используются и варианты получения?",
    answer: "Работаю только с профессиональными материалами высокого качества: масляные краски Winsor & Newton, акриловые Amsterdam и Liquitex, холсты на подрамнике из 100% хлопка. Доставка по Омску бесплатно, самовывоз из мастерской возможен по предварительной договоренности.",
    category: "Материалы и доставка"
  },
  {
    id: "commission-process",
    question: "Как оформить заказ на картину?",
    answer: "Процесс заказа прост и удобен: 1) Связываетесь со мной любым удобным способом 2) Обсуждаем ваши пожелания, размер, технику 3) Согласовываем эскиз 4) Вносите предоплату 50% 5) Приступаю к работе 6) Готовую картину получаете с доплатой остальной суммы.",
    category: "Сроки и процесс"
  },
  {
    id: "services-styles",
    question: "Какие техники и стили живописи доступны?",
    answer: "Специализируюсь на живописи маслом и акрилом в различных жанрах: пейзажи, натюрморты, абстракции. Работаю в стилях от классического реализма до современной стилизации. Также выполняю роспись стен и провожу индивидуальные мастер-классы.",
    category: "Техники и услуги"  
  },
  {
    id: "wall-painting",
    question: "Выполняете ли роспись стен?",
    answer: "Да, занимаюсь художественной росписью стен в квартирах, домах, офисах и общественных помещениях. Работаю акриловыми красками, которые безопасны и долговечны. Разрабатываю эскиз согласно вашим пожеланиям и особенностям интерьера.",
    category: "Техники и услуги"
  },
  {
    id: "master-classes",
    question: "Проводите ли обучение живописи?",
    answer: "Провожу индивидуальные мастер-классы и групповые занятия по живописи маслом и акрилом. Подходит как для начинающих, так и для тех, кто хочет улучшить свои навыки. Длительность занятия 2-3 часа, все материалы предоставляются.",
    category: "Обучение"
  }
];

const FAQ = () => {
  const [openItems, setOpenItems] = useState<string[]>([]);

  const toggleItem = (id: string) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const categories = [...new Set(faqData.map(item => item.category))];

  const generateFAQSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": "Часто задаваемые вопросы о художнике Валерии Вагнер из Омска",
      "description": "Ответы на популярные вопросы о заказе картин, росписи стен и художественных услугах в Омске",
      "mainEntity": faqData.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema(), null, 2)
        }}
      />
      
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8" aria-labelledby="faq-title">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 id="faq-title" className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary via-primary-glow to-accent bg-clip-text text-transparent">
              Часто задаваемые вопросы
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Все что нужно знать о заказе картин, ценах и процессе работы с художником Валерией Вагнер
            </p>
          </div>

          <div className="space-y-6">
            {categories.map((category, categoryIndex) => (
              <div key={category} className="space-y-3">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary to-primary-glow flex items-center justify-center">
                    <span className="text-sm font-bold text-primary-foreground">{categoryIndex + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground">
                    {category}
                  </h3>
                </div>
                
                {faqData
                  .filter(item => item.category === category)
                  .map((item) => (
                    <Card key={item.id} className="gallery-card overflow-hidden">
                      <CardContent className="p-0">
                        <button
                          className="w-full text-left p-5 md:p-6 focus:outline-none focus:ring-2 focus:ring-primary/30 hover:bg-muted/30 transition-all duration-300"
                          onClick={() => toggleItem(item.id)}
                          aria-expanded={openItems.includes(item.id)}
                          aria-controls={`faq-answer-${item.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <h4 className="text-base md:text-lg font-semibold text-foreground pr-2 leading-relaxed">
                              {item.question}
                            </h4>
                            <div className={`flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center transition-transform duration-300 ${openItems.includes(item.id) ? 'rotate-180' : ''}`}>
                              <ChevronDown className="w-4 h-4 text-primary" />
                            </div>
                          </div>
                        </button>
                        
                        {openItems.includes(item.id) && (
                          <div 
                            id={`faq-answer-${item.id}`}
                            className="px-5 md:px-6 pb-5 md:pb-6 animate-in slide-in-from-top-2 duration-300"
                          >
                            <div className="pt-4 border-t border-border/50">
                              <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-4">
                                <p className="text-base text-foreground/90 leading-relaxed font-light">
                                  {item.answer}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                }
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 md:p-8 bg-gradient-to-r from-card to-muted/30 rounded-xl border border-border/50 shadow-lg">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-3 text-foreground">Остались вопросы?</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Свяжитесь со мной любым удобным способом — отвечу на все ваши вопросы о заказе картин
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                <a
                  href="https://vk.com/im?sel=-214021218&entrypoint=community_page"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-semibold rounded-lg hover:from-primary-glow hover:to-primary transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zM18.947 8.684l-1.579 7.368c-.105.526-.421.631-.842.421l-2.368-1.684-1.158 1.105c-.105.105-.21.21-.421.21l.158-2.263 3.789-3.368c.158-.158-.053-.263-.263-.105l-4.631 2.842-2.105-.631c-.447-.158-.447-.447.105-.631l8.21-3.158c.421-.105.789.105.631.631z"/>
                  </svg>
                  ВКонтакте
                </a>
                <a
                  href="https://wa.me/79507818785?text=Здравствуйте! У меня есть вопрос о заказе картины"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.520-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;